import round from '../../../helpers/round';
import React, { useState } from 'react';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import UvsTimeChart from './UvsTimeGraph';
const ConsolidationResult = ({ result, uvtContainerRef }) => {
  const { z1, z2, df, b, cv, drainageType, l, mv, q, t } = result.inputs;

  const {
    canUserSeeCalculationSteps,
    midDepth,
    midDepthPath,
    stressIncrease,
    compressibleLayerThickness,
    compressibleLayerPathID,
    soedM,
    soedCm,
    sbcf,
    clayTypeText,
    s,
    drainagePath,
    drainageTypeText,
    tv,
    u,
    sFinished,
    sRemaining,
    series,
  } = result.outputs;

  const isSmallScreen = useMediaQuery('(max-width:700px)');

  const StressIncrease = () => {
    const [showZDetails, setShowZDetails] = useState(false);
    const [showQzDetails, setShowQzDetails] = useState(false);
    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Stress Increase
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            To calculate the stress increase at the midpoint of the compressible layer due to the foundation load, the distance
            from the bottom of the foundation to the midpoint of the compressible zone (z) should be determined first. The stress
            increase, q<sub>z</sub>, can then be calculated using the 2:1 method.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* z  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowZDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showZDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>z</b> (Distance from bottom of foundation to middle of compression zone):{' '}
                {midDepth || midDepth === 0 ? round(midDepth, 2) : '—'} m
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showZDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    This z value is shown on the sketch visually. It is calculated depending on the relative positions of the
                    foundation base and the compressible layer as follows:
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 1 : Foundation base is above the compressible layer (d<sub>f</sub> &le; z<sub>1</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>z</mi>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <msub>
                            <mi>z</mi>
                            <mi>1</mi>
                          </msub>
                          <mo>+</mo>
                          <msub>
                            <mi>z</mi>
                            <mi>2</mi>
                          </msub>
                        </mrow>
                        <mrow>
                          <mi>2</mi>
                        </mrow>
                      </mfrac>
                      <mo>&#x2212;</mo>
                      <msub>
                        <mi>d</mi>
                        <mi>f</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 2 : Foundation base is inside the compressible layer (z<sub>2</sub> &gt; d<sub>f</sub> &gt; z<sub>1</sub>
                    )
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>z</mi>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <msub>
                            <mi>z</mi>
                            <mi>2</mi>
                          </msub>
                          <mo>+</mo>
                          <msub>
                            <mi>d</mi>
                            <mi>f</mi>
                          </msub>
                        </mrow>
                        <mrow>
                          <mi>2</mi>
                        </mrow>
                      </mfrac>
                      <mo>&#x2212;</mo>
                      <msub>
                        <mi>d</mi>
                        <mi>f</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 3 : Foundation base is below the compressible layer ( d<sub>f</sub> &ge; z<sub>2</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>z</mi>
                      <mo>=</mo>
                      <mi>0 m</mi>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since we are in <b>Case {midDepthPath}</b>, z is calculated as follows:
                  </Typography>
                  {midDepthPath === 1 && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      <math display="block">
                        <mi>z</mi>
                        <mo>=</mo>
                        <mfrac>
                          <mrow>
                            <mi>{z1 ? round(z1, 2) : '—'}</mi>
                            <mo>+</mo>
                            <mi>{z2 ? round(z2, 2) : '—'}</mi>
                          </mrow>
                          <mrow>
                            <mi>2</mi>
                          </mrow>
                        </mfrac>
                        <mo>&#x2212;</mo>
                        <mi>
                          <mi>{df ? round(df, 2) : '—'}</mi>
                        </mi>
                      </math>
                    </Typography>
                  )}
                  {midDepthPath === 2 && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      <math display="block">
                        <mi>z</mi>
                        <mo>=</mo>
                        <mfrac>
                          <mrow>
                            <mi>{z2 ? round(z2, 2) : '—'}</mi>
                            <mo>+</mo>
                            <mi>{df ? round(df, 2) : '—'}</mi>
                          </mrow>
                          <mrow>
                            <mi>2</mi>
                          </mrow>
                        </mfrac>
                        <mo>&#x2212;</mo>
                        <mi>
                          <mi>{df ? round(df, 2) : '—'}</mi>
                        </mi>
                      </math>
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mi>z</mi>
                      <mo>=</mo>
                      <mi>{midDepth || midDepth === 0 ? round(midDepth, 2) : '—'} m</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* qz  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowQzDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showQzDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  q<sub>z</sub>
                </b>{' '}
                (Stress increse at depth z): {stressIncrease ? round(stressIncrease, 2) : '—'} kPa
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showQzDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Stress increase (q<sub>z</sub>) at midpoint of compressible layer due to foundation load (q) is calculated by
                    2/1 method as follows:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>z</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <mi>q</mi>
                          <mo>&#x00D7;</mo>
                          <mi>B</mi>
                          <mo>&#x00D7;</mo>
                          <mi>L</mi>
                        </mrow>
                        <mrow>
                          <mi>(B+z)</mi>
                          <mo>&#x00D7;</mo>
                          <mi>(L+z)</mi>
                        </mrow>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since we have already calculated z as {midDepth || midDepth === 0 ? round(midDepth, 2) : '—'} m, and we know
                    the foundation load q as {q ? round(q, 2) : '—'} kPa, foundation width B as {b ? round(b, 2) : '—'} m and
                    foundation length L as {l ? round(l, 2) : '—'} m, we can substitute these values into the formula to calculate
                    q<sub>z</sub> as follows:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>z</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <mi>{q ? round(q, 2) : '—'}</mi>
                          <mo>&#x00D7;</mo>
                          <mi>{b ? round(b, 2) : '—'}</mi>
                          <mo>&#x00D7;</mo>
                          <mi>{l ? round(l, 2) : '—'}</mi>
                        </mrow>
                        <mrow>
                          <mi>
                            ({b ? round(b, 2) : '—'}+{midDepth || midDepth === 0 ? round(midDepth, 2) : '—'})
                          </mi>
                          <mo>&#x00D7;</mo>
                          <mi>
                            ({l ? round(l, 2) : '—'}+{midDepth || midDepth === 0 ? round(midDepth, 2) : '—'})
                          </mi>
                        </mrow>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>q</mi>
                        <mi>z</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>{stressIncrease ? round(stressIncrease, 2) : '—'} kPa</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  const Settlement = () => {
    const [showHDetails, setShowHDetails] = useState(false);
    const [showSoedDetails, setShowSoedDetails] = useState(false);
    const [showSettlementDetails, setShowSettlementDetails] = useState(false);
    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Consolidation Settlement
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            To calculate the settlement of the foundation due to consolidation of the compressible soil layer, first the thickness
            of the compressible layer considering the foundation depth (d<sub>f</sub>) should be determined. Then, the primary
            consolidation settlement (S<sub>oed</sub>) can be calculated. Finally, by applying the Skempton-Bjerrum correction
            factor, the final settlement (S) can be obtained.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* H  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowHDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showHDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>H</b> (Compressible layer thickness under the foundation):{' '}
                {compressibleLayerThickness || compressibleLayerThickness === 0 ? round(compressibleLayerThickness, 2) : '—'} m
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showHDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    This H value is shown on the sketch visually. It is calculated depending on the relative positions of the
                    foundation base and the compressible layer as follows:
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 1 : Foundation base is above the compressible layer (d<sub>f</sub> &le; z<sub>1</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>H</mi>
                      <mo>=</mo>
                      <msub>
                        <mi>z</mi>
                        <mi>2</mi>
                      </msub>
                      <mo>&#x2212;</mo>
                      <msub>
                        <mi>z</mi>
                        <mi>1</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 2 : Foundation base is inside the compressible layer (z<sub>2</sub> &gt; d<sub>f</sub> &gt; z<sub>1</sub>
                    )
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>H</mi>
                      <mo>=</mo>
                      <msub>
                        <mi>z</mi>
                        <mi>2</mi>
                      </msub>
                      <mo>&#x2212;</mo>
                      <msub>
                        <mi>d</mi>
                        <mi>f</mi>
                      </msub>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Case 3 : Foundation base is below the compressible layer ( d<sub>f</sub> &ge; z<sub>2</sub>)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>H</mi>
                      <mo>=</mo>
                      <mi>0 m</mi>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since we are in <b>Case {compressibleLayerPathID}</b>, H is calculated as follows:
                  </Typography>
                  {compressibleLayerPathID === 1 && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      <math display="block">
                        <mi>H</mi>
                        <mo>=</mo>
                        <mi>{z2 ? round(z2, 2) : '—'}</mi>
                        <mo>&#x2212;</mo>
                        <mi>{z1 ? round(z1, 2) : '—'}</mi>
                      </math>
                    </Typography>
                  )}
                  {compressibleLayerPathID === 2 && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      <math display="block">
                        <mi>H</mi>
                        <mo>=</mo>
                        <mi>{z2 ? round(z2, 2) : '—'}</mi>
                        <mo>&#x2212;</mo>
                        <mi>{df ? round(df, 2) : '—'}</mi>
                      </math>
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mi>H</mi>
                      <mo>=</mo>
                      <mi>
                        {compressibleLayerThickness || compressibleLayerThickness === 0
                          ? round(compressibleLayerThickness, 2)
                          : '—'}{' '}
                        m
                      </mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* S oed  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSoedDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSoedDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  S<sub>oed</sub>
                </b>{' '}
                (Primary consolidation settlement): {soedCm || soedCm === 0 ? round(soedCm, 2) : '—'} cm
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSoedDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    S<sub>oed</sub> is calculated using the formula:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>S</mi>
                        <mtext>oed</mtext>
                      </msub>
                      <mo>=</mo>
                      <mi>H</mi>
                      <mo>×</mo>
                      <msub>
                        <mi>m</mi>
                        <mi>v</mi>
                      </msub>
                      <mo>×</mo>
                      <msub>
                        <mi>q</mi>
                        <mi>z</mi>
                      </msub>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Since we have already calculated H as{' '}
                    {compressibleLayerThickness || compressibleLayerThickness === 0 ? round(compressibleLayerThickness, 2) : '—'}{' '}
                    m and q<sub>z</sub> as {stressIncrease ? round(stressIncrease, 2) : '—'} kPa, and we know m<sub>v</sub> as{' '}
                    {mv} x 10<sup>-5</sup> m<sup>2</sup>/kN, we can substitute these values into the formula to calculate S
                    <sub>oed</sub> as follows:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>S</mi>
                        <mtext>oed</mtext>
                      </msub>
                      <mo>=</mo>
                      <mi>
                        {compressibleLayerThickness || compressibleLayerThickness === 0
                          ? round(compressibleLayerThickness, 2)
                          : '—'}
                      </mi>
                      <mo>×</mo>
                      <mo>(</mo>
                      <mi>{mv || mv === 0 ? round(mv, 2) : '—'}</mi>
                      <mo>×</mo>
                      <msup>
                        <mi>10</mi>
                        <mi>-5</mi>
                      </msup>
                      <mo>)</mo>

                      <mo>×</mo>
                      <mi>{stressIncrease ? round(stressIncrease, 2) : '—'}</mi>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And so, the primary consolidation settlement S<sub>oed</sub> is calculated as:
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>S</mi>
                        <mtext>oed</mtext>
                      </msub>
                      <mo>=</mo>
                      <mi>{soedM || soedM === 0 ? round(soedM, 3) : '—'}</mi>
                      <mi> m</mi>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    Or,
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <msub>
                        <mi>S</mi>
                        <mtext>oed</mtext>
                      </msub>
                      <mo>=</mo>
                      <mi>{soedCm || soedCm === 0 ? round(soedCm, 2) : '—'}</mi>
                      <mi> cm</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* S */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowSettlementDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showSettlementDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>S</b> (Final consolidation settlement): {s || s === 0 ? round(s, 2) : '—'} cm
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showSettlementDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    To calculate the final consolidation settlement (S), we apply the Skempton-Bjerrum correction factor (&mu;) to
                    the primary consolidation settlement (S<sub>oed</sub>) as follows:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <math display="block">
                      <mi>s</mi>
                      <mo>=</mo>
                      <mi>μ</mi>
                      <mo>×</mo>
                      <msub>
                        <mi>S</mi>
                        <mtext>oed</mtext>
                      </msub>
                    </math>
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    The Skempton–Bjerrum correction factor (μ) is selected according to the clay consistency. Typical values are
                    listed below.
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={0.5}>
                    • Sensitive clays, soft alluvial clays: μ ≈ 1.0–1.2
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={0.5}>
                    • Normally consolidated clays: μ ≈ 0.7–1.0
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={0.5}>
                    • Overconsolidated clays: μ ≈ 0.5–0.7
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={2}>
                    • Heavily overconsolidated clays: μ ≈ 0.2–0.5
                  </Typography>

                  <Typography color="text.secondary" variant="body2" mb={1}>
                    Selected clay type: {clayTypeText || 'the chosen clay type'} &rarr; μ = {sbcf || sbcf === 0 ? sbcf : '—'} (To
                    be on the safe side, the larger value of the range is selected.)
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    And so, the final consolidation settlement S is calculated as:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mi>S</mi>
                      <mo>=</mo>
                      <mi>{sbcf || sbcf === 0 ? sbcf : '—'}</mi>
                      <mo>×</mo>

                      <mi>{soedCm || soedCm === 0 ? round(soedCm, 2) : '—'}</mi>
                    </math>
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mi>S</mi>
                      <mo>=</mo>
                      <mi>{s || s === 0 ? round(s, 2) : '—'}</mi>
                      <mi> cm</mi>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  const SettlementAfterT = () => {
    const [showHDrDetails, setShowHDrDetails] = useState(false);
    const [showTvDetails, setShowTvDetails] = useState(false);
    const [showUDetails, setShowUDetails] = useState(false);
    const [showUvsTDetails, setShowUvsTDetails] = useState(false);

    return (
      <Box sx={{ my: 1, textAlign: 'justify' }}>
        <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
          <Typography variant="subtitle1" color="text.secondary" fontWeight={600} gutterBottom>
            Consolidation Settlement (at elapsed time t)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            To calculate the settlement of the foundation due to consolidation of the compressible soil layer at an elapsed time
            t, first the drainage path length (H<sub>dr</sub>) of the compressible layer should be determined. Then the time
            factor (T
            <sub>v</sub>) can be calculated. Using this, the degree of consolidation (U) can be found. Finally, the settlement at
            time t (S<sub>t</sub>) can be obtained.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Hdr  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowHDrDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showHDrDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  H<sub>dr</sub>
                </b>{' '}
                (Length of the drainage path): {drainagePath || drainagePath === 0 ? round(drainagePath, 2) : '—'} m
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showHDrDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={1} mt={2}>
                    H<sub>dr</sub> is the maximum distance that pore water must travel to reach a drainage boundary. Since the
                    drainage type is selected as "<b>{drainageTypeText || 'the chosen drainage type'}</b>", the drainage path
                    length is equal to {drainageTypeText === 'two way' && 'half the'} compressible layer thickness (H) calculated
                    previously:
                  </Typography>

                  {drainageTypeText === 'one way' && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      <math display="block">
                        <msub>
                          <mi>H</mi>
                          <mi>dr</mi>
                        </msub>
                        <mo>=</mo>
                        <mi>H</mi>
                        <mo>=</mo>
                        <mi>{drainagePath || drainagePath === 0 ? round(drainagePath, 2) : '—'} m</mi>
                      </math>
                    </Typography>
                  )}
                  {drainageTypeText === 'two way' && (
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      <math display="block">
                        <msub>
                          <mi>H</mi>
                          <mi>dr</mi>
                        </msub>
                        <mo>=</mo>
                        <mi>H</mi>
                        <mi>/</mi>
                        <mi>2</mi>
                        <mo>=</mo>

                        <mi>
                          {compressibleLayerThickness || compressibleLayerThickness === 0
                            ? round(compressibleLayerThickness, 2)
                            : '—'}
                        </mi>
                        <mi>/</mi>
                        <mi>2</mi>
                        <mo>=</mo>
                        <mi>{drainagePath || drainagePath === 0 ? round(drainagePath, 2) : '—'} m</mi>
                      </math>
                    </Typography>
                  )}
                </Collapse>
              )}
            </Alert>
            {/* Tv  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowTvDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showTvDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>
                  T<sub>v</sub>
                </b>{' '}
                (Time factor): {tv || tv === 0 ? round(tv, 3) : '—'} (unitless)
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showTvDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    We have alredy calculated the drainage path length H<sub>dr</sub> as{' '}
                    {drainagePath || drainagePath === 0 ? round(drainagePath, 2) : '—'} m. And also, we know the coefficient of
                    consolidation c<sub>v</sub> as {cv} x 10<sup>-8</sup> m<sup>2</sup>/s and the elapsed time t as {t} months.
                    Now, we can substitute these values into the formula to calculate T<sub>v</sub> as follows:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>T</mi>
                        <mi>v</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <msub>
                            <mi>c</mi>
                            <mi>v</mi>
                          </msub>
                          <mo>×</mo>
                          <mi>t</mi>
                        </mrow>
                        <msup>
                          <msub>
                            <mi>H</mi>
                            <mi>dr</mi>
                          </msub>
                          <mn>2</mn>
                        </msup>
                      </mfrac>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <msub>
                        <mi>T</mi>
                        <mi>v</mi>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mrow>
                          <mi>{cv || cv === 0 ? round(cv, 3) : '—'}</mi>
                          <mo>×</mo>

                          <msup>
                            <mi>10</mi>
                            <mi>-8</mi>
                          </msup>
                          <mo>×</mo>
                          <mo>(</mo>
                          <mi>{t || t === 0 ? round(t, 2) : '—'}</mi>
                          <mo>×</mo>
                          <mi>30</mi>
                          <mo>×</mo>
                          <mi>24</mi>
                          <mo>×</mo>
                          <mi>60</mi>
                          <mo>×</mo>
                          <mi>60</mi>
                          <mo>)</mo>
                        </mrow>
                        <msup>
                          <mi>{drainagePath || drainagePath === 0 ? round(drainagePath, 2) : '—'}</mi>
                          <mn>2</mn>
                        </msup>
                      </mfrac>
                      <mo>=</mo>
                      <mi>{tv || tv === 0 ? round(tv, 3) : '—'}</mi>
                      <mo>(unitless)</mo>
                    </math>
                  </Typography>
                </Collapse>
              )}
            </Alert>
            {/* U  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowUDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showUDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>U</b> (Degree of consolidation): {u || u === 0 ? round(u, 2) : '—'} %
              </Typography>
              {canUserSeeCalculationSteps && (
                <Collapse in={showUDetails} unmountOnExit>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    We have alredy calculated the time factor T<sub>v</sub> as {tv || tv === 0 ? round(tv, 3) : '—'}. Now, we can
                    use this to find the degree of consolidation U (%) using the following relationships:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    <math display="block">
                      <mrow>
                        <mn>0</mn>
                        <mo>≤</mo>
                        <mi>U</mi>
                        <mo>(%)</mo>
                        <mo>≤</mo>
                        <mn>60</mn>
                        <mo>→</mo>
                        <mi>U</mi>
                        <mo>(%)</mo>
                        <mo>=</mo>
                        <mn>100</mn>
                        <mo>×</mo>
                        <msup>
                          <mrow>
                            <mo>(</mo>
                            <mfrac>
                              <mrow>
                                <mn>4</mn>
                                <mo>×</mo>
                                <msub>
                                  <mi>T</mi>
                                  <mi>v</mi>
                                </msub>
                              </mrow>
                              <mi>π</mi>
                            </mfrac>
                            <mo>)</mo>
                          </mrow>
                          <mn>0.5</mn>
                        </msup>
                      </mrow>
                    </math>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mrow>
                        <mn>60</mn>
                        <mo>&lt;</mo>
                        <mi>U</mi>
                        <mo>(%)</mo>
                        <mo>≤</mo>
                        <mn>100</mn>
                        <mo>→</mo>
                        <mi>U</mi>
                        <mo>(%)</mo>
                        <mo>=</mo>
                        <mn>100</mn>
                        <mo>-</mo>
                        <msup>
                          <mn>10</mn>
                          <mfrac>
                            <mrow>
                              <mn>1.781</mn>
                              <mo>-</mo>
                              <msub>
                                <mi>T</mi>
                                <mi>v</mi>
                              </msub>
                            </mrow>
                            <mn>0.933</mn>
                          </mfrac>
                        </msup>
                      </mrow>
                    </math>
                  </Typography>
                  <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                    From here, by substituting the calculated T<sub>v</sub> value into the appropriate equation based on its
                    range, we can find the degree of consolidation U (%):
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <math display="block">
                      <mi>U</mi>
                      <mo>=</mo>
                      <mi>{u || u === 0 ? round(u, 2) : '—'}</mi>
                      <mo>%</mo>
                    </math>
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-evenly',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    {/* Total */}
                    <Box sx={{ flex: '1 1 160px', mx: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Total (cm)
                      </Typography>
                      <Typography variant="body1" fontWeight={400}>
                        {s !== undefined ? round(s, 2) : '—'}
                      </Typography>
                    </Box>

                    {/* Time */}
                    <Box sx={{ flex: '1 1 160px', mx: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Time (month)
                      </Typography>
                      <Typography variant="body1" fontWeight={400}>
                        {t !== undefined ? round(t, 0) : '—'}
                      </Typography>
                    </Box>

                    {/* Completed */}
                    <Box sx={{ flex: '1 1 160px', mx: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Completed (cm)
                      </Typography>
                      <Typography variant="body1" fontWeight={400}>
                        {sFinished !== undefined ? round(sFinished, 2) : '—'}
                      </Typography>
                    </Box>

                    {/* Remaining */}
                    <Box sx={{ flex: '1 1 160px', mx: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={500}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Remaining (cm)
                      </Typography>
                      <Typography variant="body1" fontWeight={400}>
                        {sRemaining !== undefined ? round(sRemaining, 2) : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              )}
            </Alert>
            {/* U Vs t  */}
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={{
                backgroundColor: 'rgba(46,125,50,0.04)',
                alignItems: 'flex-start', // keep icon & action pinned to top
                '& .MuiAlert-icon': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  minWidth: 24, // fixed width for left icon column
                },
                '& .MuiAlert-action': {
                  alignSelf: 'flex-start',
                  mt: 0.25,
                  // fix the action column width so the button never shifts
                  width: 32,
                  justifyContent: 'center',
                  p: 0,
                  ml: 1,
                },
                '& .MuiAlert-message': { width: '100%' }, // let message grow downward
              }}
              action={
                canUserSeeCalculationSteps ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setShowUvsTDetails((v) => !v)}
                    sx={{ width: 32, height: 32 }} // matches action width above
                  >
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{
                        transform: showUvsTDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                    />
                  </IconButton>
                ) : (
                  <Tooltip
                    title={
                      <span>
                        Upgrade to unlock calculation details.{' '}
                        <Link
                          href="/pricing"
                          target="_blank"
                          rel="noopener"
                          style={{ color: '#90caf9', textDecoration: 'underline' }}
                        >
                          See plans
                        </Link>
                      </span>
                    }
                    leaveDelay={500}
                    arrow
                  >
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: 'text.disabled',
                        cursor: 'not-allowed',
                        mr: 1,
                        mt: 0.5,
                      }}
                    />
                  </Tooltip>
                )
              }
            >
              <Typography color="text.secondary" variant="body2">
                <b>U vs T Graph</b> (Degree of consolidation Vs Time)
              </Typography>
              {canUserSeeCalculationSteps && (
                <>
                  <Collapse in={showUvsTDetails} unmountOnExit>
                    <Typography color="text.secondary" variant="body2" mb={2} mt={2}>
                      The graph below illustrates the relationship between the degree of consolidation (U) and time (t) for the
                      given soil and loading conditions. It shows how consolidation progresses over time. The graph is plotted
                      until degree of consolidation reaches 90% or until the input time limit (360 months) is reached.
                    </Typography>
                    <UvsTimeChart series={series} />
                  </Collapse>

                  <div
                    ref={uvtContainerRef}
                    style={{
                      position: 'absolute',
                      left: -99999,
                      top: -99999,
                      width: 800,
                      height: 350,
                      pointerEvents: 'none',
                      overflow: 'hidden',
                    }}
                  >
                    <UvsTimeChart series={series} />
                  </div>
                </>
              )}
            </Alert>
          </Box>
        </Alert>
      </Box>
    );
  };
  return (
    <section className="consolidationOutput">
      <StressIncrease />
      <Settlement />
      <SettlementAfterT />
    </section>
  );
};

export default ConsolidationResult;
